// Interfaces
import type { TermPage } from 'interfaces/content/TermPage';
import dynamic from 'next/dynamic';
import type React from 'react';
import type { JSX } from 'react';

// Constants
import { UIFragments } from 'utils/log/constants/uiFragments';
import RawHtml from 'utils/miscUtils/rawHtml';
// Utils
import { scrubContent } from 'utils/stringUtils';

// Components

import ErrorBoundary from 'components/ErrorBoundary';

// Styles
import * as S from './TermNextPageStyles';

const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));
const ComplexityTag = dynamic(() => import('components/ComplexityTag'));
const SocialShareDropdown = dynamic(() => import('components/SocialShareDropdown'));

const ContentWithEmbed = dynamic(() => import('components/ContentWithEmbed'), {
  ssr: false,
});

export interface TermPageProps {
  term: TermPage;
  sidebar?: JSX.Element[];
  adFrequency: number;
  embeds: { node: string; index: number; component: JSX.Element }[];
}

const TermNextPage: React.FC<TermPageProps> = ({ term, sidebar, adFrequency, embeds }) => {
  const cleanedContent = scrubContent(term.content);

  const breadcrumbs = <Breadcrumbs {...term.breadcrumbs} />;

  return (
    <div id='content'>
      <S.ContentContainer>
        {breadcrumbs}
        <S.ContentAndSidebarContainer>
          <div>
            <S.ComplexityWrapper>
              <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_TERM_PAGE}>
                <ComplexityTag complexity={term.complexity} />
              </ErrorBoundary>
            </S.ComplexityWrapper>
            <S.TitleContainer>
              <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_TERM_PAGE}>
                <S.TitleWrapper
                  as='h1'
                  $fontFamily='graphikCond'
                  $size={{ default: 'level1' }}
                >
                  {term.title}
                </S.TitleWrapper>
              </ErrorBoundary>
              <S.ShareWrapper>
                <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_TERM_PAGE}>
                  <SocialShareDropdown
                    title={term.title}
                    className='share-with-label'
                    variant='primary'
                  />
                </ErrorBoundary>
              </S.ShareWrapper>
            </S.TitleContainer>
            <S.ContentWrapper>
              <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_TERM_PAGE}>
                <ContentWithEmbed
                  embedInstructions={embeds.map((embed) => ({ index: embed.index, node: embed.node }))}
                  adFrequency={adFrequency}
                  section={term.primarySection.name}
                  articleUrl={term.url}
                >
                  {[
                    ...embeds.map((embed) => embed.component),
                    <div
                      key='term-content'
                      className='rawHtml-content'
                    >
                      {RawHtml({ html: cleanedContent })}
                    </div>,
                  ]}
                </ContentWithEmbed>
              </ErrorBoundary>
            </S.ContentWrapper>
          </div>
          <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_TERM_PAGE}>
            <S.SidebarContainer>{sidebar}</S.SidebarContainer>
          </ErrorBoundary>
        </S.ContentAndSidebarContainer>
      </S.ContentContainer>
    </div>
  );
};

export default TermNextPage;
